import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, Link as LinkIcon, MailOpen, Search } from 'lucide-react';
import postSaleApi from 'api/requests/postSale';
import EmailPreviewFrame from 'modules/dashboard/producer/customers/EmailPreviewFrame';
import date from 'helpers/date';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const OPENED_FILTERS = [
    { value: '', label: 'Todos' },
    { value: '1', label: 'Abriram' },
    { value: '0', label: 'Não abriram' },
];

const SEARCH_DELAY_MS = 350;

function percent(part, whole) {
    return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/**
 * Pós-venda do evento, na aba Ingressos: o recado que cada ingresso manda no
 * e-mail de pedido aprovado, quantos compradores o receberam e quem já abriu.
 * Só aparece quando algum ingresso tem recado (ou já teve envios).
 */
export default function PostSaleSection({ eventSlug }) {
    const [summary, setSummary] = useState(null);
    const [summaryFailed, setSummaryFailed] = useState(false);
    const [productId, setProductId] = useState(null);

    const [opened, setOpened] = useState('');
    const [searchText, setSearchText] = useState('');
    const [term, setTerm] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;

        postSaleApi.get(eventSlug).then(({ data }) => {
            if (!active) return;
            const payload = data?.data || { tickets: [], totals: { sent: 0, opened: 0 } };
            setSummary(payload);
            const first = payload.tickets.find((ticket) => ticket.message_html || ticket.link_url) || payload.tickets[0];
            setProductId(first ? first.product_id : null);
        }).catch((err) => {
            // Sem permissão para ver os compradores: a seção simplesmente não existe.
            if (active && err?.response?.status !== 403) setSummaryFailed(true);
        });

        return () => { active = false; };
    }, [eventSlug]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setTerm(searchText.trim());
            setPage(1);
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (!productId) return undefined;

        let active = true;
        setLoading(true);
        setError(null);

        postSaleApi.deliveries(eventSlug, {
            product_id: productId,
            opened: opened === '' ? undefined : opened,
            term: term || undefined,
            page,
        }).then(({ data }) => {
            if (!active) return;
            setDeliveries(data?.data || []);
            setLastPage(data?.meta?.last_page || 1);
        }).catch(() => {
            if (active) setError('Não foi possível carregar a lista.');
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => { active = false; };
    }, [eventSlug, productId, opened, term, page]);

    function selectTicket(id) {
        setProductId(id);
        setOpened('');
        setSearchText('');
        setTerm('');
        setPage(1);
    }

    function changeOpened(value) {
        setOpened(value);
        setPage(1);
    }

    if (summaryFailed) {
        return (
            <Section>
                <Title>Pós-venda</Title>
                <StateText $error>Não foi possível carregar o pós-venda.</StateText>
            </Section>
        );
    }

    if (!summary || summary.tickets.length === 0) return null;

    const ticket = summary.tickets.find((item) => item.product_id === productId) || summary.tickets[0];
    const hasText = !!ticket.message_html;

    return (
        <Section>
            <Head>
                <div>
                    <Title>Pós-venda</Title>
                    <Subtitle>O recado que vai no e-mail de pedido aprovado e quem já o recebeu.</Subtitle>
                </div>
                <Totals>
                    <TotalChip title='E-mails de pedido aprovado que levaram um recado'>
                        <TotalValue>{summary.totals.sent}</TotalValue>
                        <TotalLabel>E-mails enviados</TotalLabel>
                    </TotalChip>
                    <TotalChip $accent title='E-mails abertos, entre os enviados'>
                        <TotalValue>{summary.totals.opened}</TotalValue>
                        <TotalLabel>Abertos · {percent(summary.totals.opened, summary.totals.sent)}%</TotalLabel>
                    </TotalChip>
                </Totals>
            </Head>

            {summary.tickets.length > 1 && (
                <Pills role='group' aria-label='Ingresso do recado'>
                    {summary.tickets.map((item) => (
                        <Pill
                            key={item.product_id}
                            type='button'
                            $active={item.product_id === ticket.product_id}
                            aria-pressed={item.product_id === ticket.product_id}
                            onClick={() => selectTicket(item.product_id)}
                        >
                            {item.name} · {item.sent_count} {item.sent_count === 1 ? 'envio' : 'envios'}
                        </Pill>
                    ))}
                </Pills>
            )}

            <BlockLabel>Recado de {ticket.name}</BlockLabel>
            {hasText || ticket.link_url ? (
                <>
                    <EmailPreviewFrame
                        subject='Recado do produtor'
                        bodyHtml={ticket.message_html || ''}
                        emptyHint='Este ingresso tem só o botão com link, sem texto.'
                    />
                    {ticket.link_url && (
                        <LinkLine>
                            <LinkIcon size={13} />
                            <span>Botão “{ticket.link_label}”:</span>
                            <a href={ticket.link_url} target='_blank' rel='noopener noreferrer'>{ticket.link_url}</a>
                        </LinkLine>
                    )}
                </>
            ) : (
                <StateText>O recado deste ingresso foi removido. A lista abaixo mostra os envios já feitos.</StateText>
            )}

            <BlockLabel>Quem recebeu</BlockLabel>
            <Controls>
                <Pills role='group' aria-label='Filtrar por abertura'>
                    {OPENED_FILTERS.map((filter) => (
                        <Pill
                            key={filter.value}
                            type='button'
                            $active={opened === filter.value}
                            aria-pressed={opened === filter.value}
                            onClick={() => changeOpened(filter.value)}
                        >
                            {filter.label}
                        </Pill>
                    ))}
                </Pills>
                <SearchBox>
                    <Search size={14} />
                    <input
                        type='search'
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder='Buscar por nome ou e-mail'
                        aria-label='Buscar comprador'
                    />
                </SearchBox>
            </Controls>

            {loading && <StateText>Carregando...</StateText>}
            {!loading && error && <StateText $error>{error}</StateText>}
            {!loading && !error && deliveries.length === 0 && (
                <StateText>
                    {opened !== '' || term
                        ? 'Nenhum comprador com esse filtro.'
                        : 'Ninguém recebeu este recado ainda.'}
                </StateText>
            )}

            {!loading && !error && deliveries.length > 0 && (
                <>
                    <TableWrapper>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Comprador</Th>
                                    <Th $hideOnMobile>Pedido</Th>
                                    <Th $hideOnMobile>Enviado em</Th>
                                    <Th>Abriu</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map((delivery) => (
                                    <tr key={delivery.id}>
                                        <Td>
                                            <Buyer>
                                                <BuyerName>{delivery.buyer_name || '—'}</BuyerName>
                                                <BuyerEmail title={delivery.email}>{delivery.email}</BuyerEmail>
                                            </Buyer>
                                        </Td>
                                        <Td $hideOnMobile>#{delivery.order_id}</Td>
                                        <Td $hideOnMobile>{date.formatDateTime(delivery.sent_at)}</Td>
                                        <Td title={delivery.opened_at ? `${delivery.open_count} abertura(s)` : ''}>
                                            {delivery.opened_at ? (
                                                <OpenedAt><MailOpen size={13} />{date.formatDateTime(delivery.opened_at)}</OpenedAt>
                                            ) : '—'}
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrapper>

                    {lastPage > 1 && (
                        <PaginationBar>
                            <PageInfo>Página {page} de {lastPage}</PageInfo>
                            <PaginationButtons>
                                <PageBtn type='button' aria-label='Página anterior' onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                                    <ChevronLeft size={14} />
                                </PageBtn>
                                <PageBtn type='button' aria-label='Próxima página' onClick={() => setPage((p) => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>
                                    <ChevronRight size={14} />
                                </PageBtn>
                            </PaginationButtons>
                        </PaginationBar>
                    )}
                </>
            )}

            <Footnote>
                A lista começa nos envios feitos depois que o registro passou a existir; quem comprou antes não
                aparece. A abertura é uma estimativa: quem bloqueia imagens no e-mail não é contado, e alguns
                programas de e-mail marcam como aberto sem a pessoa ter lido.
            </Footnote>
        </Section>
    );
}

const Section = styled.section`
    margin-top: 8px;
    padding: 20px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const Head = styled.header`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const Title = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Subtitle = styled.p`
    margin: 2px 0 0;
    font-size: 12px;
    color: ${colors.night};
`;

const Totals = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

const TotalChip = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 104px;
    padding: 8px 12px;
    border-radius: 10px;
    background: ${({ $accent }) => ($accent ? panelAccentAlpha(colors.secondaryPurple, '12') : colors.neutralBackground)};
    border: 1px solid ${({ $accent }) => ($accent ? panelAccentAlpha(colors.secondaryPurple, '30') : colors.neutralDark)};
`;

const TotalValue = styled.span`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const TotalLabel = styled.span`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: ${colors.night};
`;

const Pills = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 14px;
`;

const Pill = styled.button`
    padding: 6px 12px;
    border-radius: 50px;
    border: 1.5px solid ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : colors.neutralDark)};
    background: ${({ $active }) => ($active ? panelAccentAlpha(colors.secondaryPurple, '12') : '#fff')};
    color: ${({ $active }) => ($active ? panelAccent(colors.secondaryPurple) : colors.nightLight)};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: ${panelAccent(colors.secondaryPurple)};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.secondaryPurple)};
        outline-offset: 2px;
    }
`;

const BlockLabel = styled.h3`
    margin: 18px 0 8px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.night};
`;

const LinkLine = styled.p`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin: 10px 0 0;
    font-size: 12px;
    color: ${colors.nightLight};

    a {
        color: ${panelAccent(colors.secondaryPurple)};
        font-weight: 600;
        word-break: break-all;
    }
`;

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
    }
`;

const SearchBox = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    margin-bottom: 14px;
    border-radius: 50px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightLight};

    input {
        border: none;
        outline: none;
        background: transparent;
        font-size: 12px;
        color: ${colors.nightDark};
        min-width: 180px;
    }

    &:focus-within {
        border-color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const StateText = styled.p`
    margin: 0;
    padding: 24px;
    text-align: center;
    font-size: 13px;
    color: ${({ $error }) => ($error ? colors.danger : colors.nightLight)};
`;

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const hideOnMobile = ({ $hideOnMobile }) => $hideOnMobile && `
    @media screen and (max-width: ${breakpoints.md}) {
        display: none;
    }
`;

const Th = styled.th`
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 8px 10px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;
    ${hideOnMobile}
`;

const Td = styled.td`
    padding: 10px;
    font-size: 13px;
    color: ${colors.nightDark};
    border-bottom: 1px solid ${colors.neutralDark};
    ${hideOnMobile}
`;

const Buyer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

const BuyerName = styled.span`
    font-weight: 600;
`;

const BuyerEmail = styled.span`
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: ${colors.nightLight};
`;

const OpenedAt = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${panelAccent(colors.secondaryPurple)};
    font-weight: 600;
    white-space: nowrap;

    @media screen and (max-width: ${breakpoints.md}) {
        white-space: normal;
    }
`;

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 12px;
`;

const PageInfo = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const PaginationButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PageBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${colors.neutralBackground};
    }
`;

const Footnote = styled.p`
    margin: 16px 0 0;
    font-size: 11px;
    line-height: 1.45;
    color: ${colors.night};
`;
